/*
 * Description:
 * ---------------------
 * Approvers: the people whose yes an AI agent needs before a big change, and
 * the passkeys they say it with. A person adds a passkey for themselves on
 * the device they hold (WebAuthn, from the server's begin route), and sees,
 * for their organisation, who is on the approver list its boxes hold, who
 * signed it, where its passkeys work, who added passkeys and is not on it
 * yet, and whether each box took it. The list itself is signed by Weavian's
 * offline root key or approved by an approver already on it, never made
 * here; the screen says what a change to it needs.
 *
 * Fed by /api/v1/mesh/approvers (approvalApi.js, server/pkg/approvals); all
 * wording is in approvalWords.js.
 *
 * ! file components/ApproversPanel.js
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertTriangle, FiCheckCircle } from "react-icons/fi";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import CustomerPick, { useCustomer } from "./CustomerPick";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { useSession } from "./PortalGate";
import { NotServed, ROUTES, ask } from "./approvalApi";
import { creationOptions, credentialJSON, passkeyBlocker } from "./passkeys";
import * as W from "./approvalWords";
import "./Firewall.css";
import "./Mesh.css";
import "./Approvals.css";

const READ_MS = 30000;

const Notice = ({ tone, children }) => (
  <div className={`fw-notice is-${tone}`} role={tone === "bad" ? "alert" : "status"}>
    {tone === "ok" ? <FiCheckCircle size={16} /> : <FiAlertTriangle size={16} />} <span>{children}</span>
  </div>
);

function Passkeys({ view, name, serverApiUrl, onAdded }) {
  const session = useSession();
  const [label, setLabel] = useState(() => W.deviceName());
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState("");
  const [problem, setProblem] = useState("");
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const mine = (view.registered || []).find((h) => h.user === session.user?.username || (!!view.you && h.name === view.you));
  const blocked = passkeyBlocker();
  const now = new Date();

  const add = async () => {
    setBusy(true);
    setProblem("");
    setMade("");
    try {
      const options = creationOptions(await ask(serverApiUrl, ROUTES.passkeyBegin, { customer: view.customer, label: label.trim() }));
      const cred = await navigator.credentials.create({ publicKey: options }).catch((e) => { throw new Error(W.passkeyError(e, true)); });
      await ask(serverApiUrl, ROUTES.passkeyFinish, credentialJSON(cred));
      if (!mounted.current) return;
      setMade(label.trim());
      onAdded();
    } catch (e) {
      if (mounted.current) setProblem(e instanceof NotServed ? W.NOT_ON_SERVER : e.message);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const form = view.passkeys_off ? <Notice tone="warn">{W.passkeysOff(view.passkeys_off)}</Notice>
    : !view.may_register ? <p className="fw-muted">{W.mayNotRegister(name)}</p>
      : blocked ? <Notice tone="warn">{W.BLOCKED[blocked]}</Notice>
        : (
          <div className="ap-add">
            <label className="ap-field">
              <span>{W.PASSKEY_NAME}</span>
              <input className="fw-input" value={label} maxLength={64} onChange={(e) => setLabel(e.target.value)} title={W.PASSKEY_NAME_HINT} />
            </label>
            <button type="button" className="fw-btn is-primary" disabled={busy || !label.trim()} onClick={add}>{busy ? W.ADDING : W.ADD_PASSKEY}</button>
          </div>
        );
  return (
    <section className="fw-card">
      <h3 className="fw-card-title">{W.YOUR_PASSKEYS}</h3>
      <p className="fw-muted">{W.PASSKEYS_LEAD}</p>
      {mine?.passkeys?.length
        ? (
          <ul className="ap-list">
            {mine.passkeys.map((p) => <li key={p.id}><strong>{p.label}</strong><span className="fw-muted">{W.passkeyLine(p, now)}</span></li>)}
          </ul>
        )
        : <p className="ap-said">{W.NO_PASSKEYS}</p>}
      {form}
      {made && <Notice tone="ok">{W.added(made)}</Notice>}
      {problem && <Notice tone="bad">{problem}</Notice>}
    </section>
  );
}

function List({ view, name }) {
  const l = view.list;
  const waiting = W.notYetListed(view);
  const now = new Date();
  return (
    <section className="fw-card">
      <h3 className="fw-card-title">{name}</h3>
      <p className="ap-said">{W.listSay(view, name)} {W.heldBy(view)}</p>
      {l && W.listWhere(l) && <p className="fw-muted">{W.listWhere(l)}</p>}
      {l?.approvers?.length > 0 && (
        <ul className="ap-list">
          {l.approvers.map((p) => <li key={p.name}><strong>{W.approverName(p, view.you)}</strong><span className="fw-muted">{W.approverLine(p)}</span></li>)}
        </ul>
      )}
      {waiting && <p className="fw-muted">{waiting}</p>}
      {l && (view.boxes || []).length > 0 && (
        <ul className="ap-boxes">
          {view.boxes.map((b) => {
            const h = W.boxHolds(b, l, now);
            return (
              <li key={b.node}>
                <span className="ap-box">{b.node}</span>
                <span className={`mesh-answer is-${h.tone}`}><span className="mesh-dot" aria-hidden="true" /> {h.text}</span>
              </li>
            );
          })}
        </ul>
      )}
      {l && (
        <details className="mesh-how">
          <summary>{W.LIST_PRINT}</summary>
          <p className="fw-muted">{W.listPrint(l)}</p>
          <code className="ap-raw">{l.digest}</code>
        </details>
      )}
    </section>
  );
}

/* One organisation's approvers, read again every 30 s. */
function Approvers({ serverApiUrl, customer, name }) {
  const [view, setView] = useState(null);
  const [error, setError] = useState("");
  const [notServed, setNotServed] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const read = useCallback(async () => {
    try {
      const out = await ask(serverApiUrl, ROUTES.approvers(customer));
      if (!mounted.current) return;
      setView(out);
      setError("");
    } catch (e) {
      if (!mounted.current) return;
      if (e instanceof NotServed) setNotServed(true);
      else setError(e.message);
    }
  }, [serverApiUrl, customer]);
  useVisibleInterval(read, READ_MS);

  if (notServed) return <FeatureUnavailableNotice compact nodeId="this Weavian server" feature="Approvers" detail={W.NOT_ON_SERVER} />;
  if (!view) return error ? <Notice tone="bad">{error}</Notice> : <p className="fw-muted">Reading…</p>;
  const shownName = name || view.customer;
  return (
    <>
      {error && <Notice tone="bad">{error}</Notice>}
      <Passkeys view={view} name={shownName} serverApiUrl={serverApiUrl} onAdded={read} />
      <List view={view} name={shownName} />
    </>
  );
}

export default function ApproversPanel({ serverApiUrl }) {
  const pick = useCustomer(serverApiUrl);
  return (
    <div className="fw">
      <section className="fw-card">
        <p className="ap-said">{W.APPROVERS_LEAD}</p>
        <CustomerPick {...pick} />
      </section>
      {pick.domains === null ? <p className="fw-muted">Reading…</p>
        : pick.none ? <section className="fw-card"><p className="ap-said">{W.NO_CUSTOMERS}</p></section>
          : <Approvers key={pick.customer} serverApiUrl={serverApiUrl} customer={pick.customer} name={pick.name} />}
      <section className="fw-card">
        <h3 className="fw-card-title">{W.CHANGE_NEEDS}</h3>
        <ol className="ap-steps">{W.CHANGE_STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
      </section>
    </div>
  );
}
